import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as buscaApi from '../../api/busca';
import { useAuth } from '../../contexts/AuthContext';
import { useEmpresaSelecionada } from '../../contexts/EmpresaContext';
import { useDebounce } from '../../hooks/useDebounce';
import { MODO_GLOBAL } from '../../utils/storageKeys';

const CATEGORIAS = [
  { chave: 'produtos', titulo: 'Produtos', icon: 'bi-basket', to: () => '/produtos' },
  { chave: 'fornecedores', titulo: 'Fornecedores', icon: 'bi-truck-flatbed', to: () => '/fornecedores' },
  { chave: 'clientes', titulo: 'Clientes', icon: 'bi-people', to: () => '/clientes' },
  { chave: 'lotes', titulo: 'Entradas (Lotes)', icon: 'bi-truck', to: (item) => `/lotes/${item.id}` },
  { chave: 'pedidos', titulo: 'Saídas (Pedidos)', icon: 'bi-cart-check', to: (item) => `/pedidos/${item.id}` },
];

function BuscaGlobal() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);
  const [resultado, setResultado] = useState(null);
  const termoDebounced = useDebounce(termo, 300);

  useEffect(() => {
    const termoLimpo = termoDebounced.trim();
    if (termoLimpo.length < 2) {
      setResultado(null);
      return;
    }
    let cancelado = false;
    buscaApi
      .buscarGlobal(termoLimpo)
      .then((r) => {
        if (!cancelado) setResultado(r.data);
      })
      .catch(() => {
        if (!cancelado) setResultado(null);
      });
    return () => {
      cancelado = true;
    };
  }, [termoDebounced]);

  useEffect(() => {
    const onClickFora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', onClickFora);
    return () => document.removeEventListener('mousedown', onClickFora);
  }, []);

  useEffect(() => {
    const onBarra = (e) => {
      const alvo = e.target;
      const digitandoEmOutroCampo =
        alvo instanceof HTMLElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName);
      if (e.key === '/' && !digitandoEmOutroCampo) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onBarra);
    return () => document.removeEventListener('keydown', onBarra);
  }, []);

  const irPara = (categoria, item) => {
    navigate(categoria.to(item));
    setAberto(false);
    setTermo('');
    setResultado(null);
  };

  const grupos = resultado ? CATEGORIAS.map((c) => ({ ...c, itens: resultado[c.chave] || [] })) : [];
  const temResultado = grupos.some((g) => g.itens.length > 0);

  return (
    <div className="sf-search" ref={ref}>
      <i className="bi bi-search" />
      <input
        ref={inputRef}
        placeholder="Buscar produto, lote, pedido…"
        aria-label="Busca global"
        value={termo}
        onChange={(e) => {
          setTermo(e.target.value);
          setAberto(true);
        }}
        onFocus={() => setAberto(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setAberto(false);
            e.currentTarget.blur();
          }
        }}
      />
      {!termo && <kbd>/</kbd>}

      {aberto && termo.trim().length >= 2 && (
        <div className="sf-search-results">
          {!resultado ? (
            <div className="sf-search-empty">Buscando...</div>
          ) : !temResultado ? (
            <div className="sf-search-empty">Nenhum resultado para "{termo.trim()}".</div>
          ) : (
            grupos
              .filter((g) => g.itens.length > 0)
              .map((g) => (
                <div key={g.chave}>
                  <div className="sf-search-group-title">{g.titulo}</div>
                  {g.itens.map((item) => (
                    <button key={item.id} type="button" className="sf-search-result" onClick={() => irPara(g, item)}>
                      <i className={`bi ${g.icon}`} />
                      <span>
                        {item.label}
                        {item.sublabel && <span className="sf-search-result-sub">{item.sublabel}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              ))
          )}
        </div>
      )}
    </div>
  );
}

function EmpresaSwitcher() {
  const { isSuperAdmin, empresas, idEmpresa, setIdEmpresa, loading, modoGlobal } = useEmpresaSelecionada();

  if (!isSuperAdmin) return null;

  const handleChange = (e) => {
    setIdEmpresa(e.target.value);
  };

  return (
    <div className={`sf-empresa-switcher ${modoGlobal ? 'sf-empresa-switcher-global' : ''}`}>
      <i className={`bi ${modoGlobal ? 'bi-globe2' : 'bi-building'}`} />
      <select
        value={idEmpresa}
        disabled={loading || empresas.length === 0}
        onChange={handleChange}
        title="Empresa que você está visualizando como super admin"
      >
        {empresas.length === 0 && <option value="">Nenhuma empresa cadastrada</option>}
        {empresas.length > 0 && <option value={MODO_GLOBAL}>Global (todas as empresas)</option>}
        {empresas.map((e) => (
          <option key={e.id_empresa} value={e.id_empresa}>
            {e.nome}
          </option>
        ))}
      </select>
    </div>
  );
}

function iniciais(nome) {
  if (!nome) return '?';
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] || '') + (partes[partes.length - 1]?.[0] || '')).toUpperCase();
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickFora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', onClickFora);
    return () => document.removeEventListener('mousedown', onClickFora);
  }, []);

  return (
    <div className="sf-user-menu" ref={ref}>
      <button type="button" className="sf-user-menu-toggle" onClick={() => setAberto((a) => !a)}>
        <span className="sf-avatar">{iniciais(user?.nome)}</span>
        <span className="d-none d-sm-flex flex-column text-start lh-sm">
          <span style={{ fontSize: '13.5px', fontWeight: 700 }}>{user?.nome}</span>
          <span style={{ fontSize: '11.5px', color: '#8b9382' }}>{user?.email}</span>
        </span>
        <i className="bi bi-chevron-down d-none d-sm-inline" style={{ fontSize: 12, color: '#8b9382' }} />
      </button>

      {aberto && (
        <div className="sf-user-menu-dropdown">
          <Link to="/perfil" className="sf-user-menu-item" onClick={() => setAberto(false)}>
            <i className="bi bi-person-gear" />
            Editar perfil
          </Link>
          <button type="button" className="sf-user-menu-item sf-user-menu-item-danger" onClick={logout}>
            <i className="bi bi-box-arrow-right" />
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar({ onToggleSidebar }) {
  return (
    <header className="sf-topbar">
      <button
        type="button"
        className="sf-icon-btn sf-icon-btn-lg"
        onClick={onToggleSidebar}
        title="Expandir/recolher menu"
        aria-label="Expandir/recolher menu"
      >
        <i className="bi bi-list" />
      </button>

      <BuscaGlobal />

      <div className="d-flex align-items-center gap-3 ms-auto">
        <EmpresaSwitcher />
        <span className="sf-topbar-divider d-none d-sm-block" />
        <UserMenu />
      </div>
    </header>
  );
}
